import type { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import type { Popup } from '../../popups/Popup';
import { BoosterType } from './types';
import type { BoosterDefinition } from './types';
import { BoosterTryPopupLayout } from '../../../layouts/boosters/BoosterTryPopupLayout';
import { applyLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';
import type { TextWithColorTags } from '../../libs/layouts/layoutPostProcessors/layoutColorTagsSPL';
import { processColorTags } from '../../libs/layouts/layoutPostProcessors/layoutColorTagsSPL';
import { setContainerParent } from '../../utils/pixi/transform-utils';

interface BoosterIntroductionPopupConfig {
  booster: BoosterDefinition;
  collectVersion: boolean;
  boosterAmount: number;
  buttonTarget?: Container;
}

export class BoosterIntroductionPopup implements Popup {
  public readonly onButtonClicked = new Signal();

  private readonly layout = new BoosterTryPopupLayout();

  public readonly root = this.layout.root;

  private readonly buttonTarget: Container | null;

  constructor(config: BoosterIntroductionPopupConfig) {
    this.buttonTarget = config.buttonTarget ?? null;
    // this.layout.boosterIcon.indicator.visible = false;
    // this.layout.boosterIcon.attractOverlay.visible = false;
    const iconStates: Record<BoosterType, string> = {
      [BoosterType.Hammer]: 'hammer-enabled',
      [BoosterType.Redraw]: 'redraw-enabled',
      [BoosterType.Undo]: 'undo-enabled',
      [BoosterType.Move]: 'move-enabled',
    };

    applyLayoutState(this.layout.boosterIcon, iconStates[config.booster.type]);

    if (config.collectVersion) {
      applyLayoutState(this.layout, 'collect');
      this.layout.boosterIcon.indicator.visible = true;
      this.layout.boosterIcon.indicatorText.text = config.boosterAmount;
    }

    this.setHeaderText(config.booster.type, config.collectVersion);

    this.layout.description.text = getLocalizedText(config.booster.description);

    this.layout.actionButton.on('pointertap', () => {
      this.onButtonClicked.emit();
    });
  }

  private setHeaderText(boosterType: BoosterType, collectVersion: boolean): void {
    const headerTexts: { collect: Record<BoosterType, string>; unlock: Record<BoosterType, string> } = {
      collect: {
        [BoosterType.Hammer]: '$Free$<br>Hammer Booster',
        [BoosterType.Redraw]: '$Free$<br>Redraw Booster',
        [BoosterType.Undo]: '$Free$<br>Undo Booster',
        [BoosterType.Move]: '$Free$<br>Move Booster',
      },
      unlock: {
        [BoosterType.Hammer]: 'Hammer Booster <br> $Unlocked!$',
        [BoosterType.Redraw]: 'Redraw Booster <br> $Unlocked!$',
        [BoosterType.Undo]: 'Undo Booster <br> $Unlocked!$',
        [BoosterType.Move]: 'Move Booster <br> $Unlocked!$',
      },
    };

    this.layout.header.text = getLocalizedText(headerTexts[collectVersion ? 'collect' : 'unlock'][boosterType]);
    processColorTags(this.layout.header as unknown as TextWithColorTags);
  }

  public async animateIconToTarget(): Promise<void> {
    if (!this.buttonTarget) return;

    setContainerParent(this.layout.boosterIcon.root, this.root.pixi.stage);

    const position = this.buttonTarget.getGlobalPosition();

    await this.layout.boosterIcon.root
      .tweenTo(
        {
          x: position.x,
          y: position.y,
          scaleXY: 1,
        },
        500,
        'quadOut',
        true,
      )
      .asyncOnComplete();

    this.layout.boosterIcon.root.destroy({ children: true });
  }

  public onAdded(): void {
    this.root.tweenFrom({ alpha: 0 }, 300, 'sinIn', true);
  }
}
